import {formatCurrency, formatPercentWithTwoDecimals} from "../../utils/format";
import React from "react";
import {StrategyDetailsSectionInfo} from "../../WealthPlanOptimizer/WealthPOTypes";
import {useAppSelector} from "../../store/hooks";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";

export type TaxType = 'Federal' | 'State';

export type GTGTExemption = {
    exemptionAmount: number;
    description: string;
}

export type RunningTotalAmounts = {
    estateValue: number;
    effectiveTaxRate: number;
    estimatedEstateTax: number;
    taxableEstateValue: number;
}

type EstateTaxRunningTotalProps = {
    taxType: TaxType;
    runningTotalAmounts: RunningTotalAmounts;
    runningTotalAmountsWithStrategies?: RunningTotalAmounts;
    charitableDeduction: number;
    additionalExemptionsOrDeductions?: { label: string, amount: number, amountWithStrategies?: number }[];
    strategyDetailsList?: StrategyDetailsSectionInfo[];
    sorDescription?: string;
    gtgtExemptions?: GTGTExemption[];
}

// NOTE : PLEASE CHECK THE IMPACT OF CHANGES TO THIS COMPONENT ON EstateTaxBreakdownReportRow

export function EstateTaxRunningTotal({
                                          taxType,
                                          runningTotalAmounts,
                                          runningTotalAmountsWithStrategies,
                                          charitableDeduction,
                                          additionalExemptionsOrDeductions = [],
                                          strategyDetailsList = [],
                                          sorDescription,
                                          gtgtExemptions = []
                                      }: Readonly<EstateTaxRunningTotalProps>) {
    const source = runningTotalAmountsWithStrategies ? "wpo" : "wealth-transfer";
    const lineItemRowClass = `line-item-row-${source}`;
    const taxRateLabel = (useAppSelector(selectReleaseToggles)?.enableEstateTaxGraduatedRates && taxType==="State") ? "Effective State Estate" : taxType;

    return <div data-testid={`${taxType}-tax-running-totals`} className="running-total">
        <div className={"section-header-background"}>
            <div className={`running-total__section-header-${source}`}>
                <div className="header-column">{taxType}</div>
                <div className="currency">
                    {formatCurrency(runningTotalAmounts.estimatedEstateTax)}
                </div>
                {runningTotalAmountsWithStrategies && <div className="currency-span-2">
                    {formatCurrency(runningTotalAmountsWithStrategies.estimatedEstateTax)}
                </div>}
            </div>
        </div>

        {runningTotalAmounts.effectiveTaxRate > 0 &&
            <div data-testid={`${taxType}-tax-running-totals-breakdown`} className={`running-total__section-${source}`}>
                <div className={lineItemRowClass}>
                    <div className={"span-1"}>Estimated Estate</div>
                    <div className={"span-2"}>{formatCurrency(runningTotalAmounts.estateValue)}</div>
                    {runningTotalAmountsWithStrategies &&
                        <div className={"span-3"}>{formatCurrency(runningTotalAmountsWithStrategies.estateValue)}</div>}
                </div>

                {strategyDetailsList?.map(({amountToBeneficiaries, description}) => (
                    <div className={lineItemRowClass} key={description}>
                        <div className="span-1"/>
                        <div className="span-2"/>
                        <div className="span-3 amt-to-beneficiaries">
                            <div className={"text-content"}>{formatCurrency(-amountToBeneficiaries)}</div>
                        </div>
                        <div className="span-4 strategy-description">
                            <div className={"text-content"}>{description}</div>
                        </div>
                    </div>
                ))}

                {charitableDeduction > 0 && <div className={lineItemRowClass}>
                    <div className={"span-1"}>Charitable Deduction</div>
                    <div className={"span-2"}>{formatCurrency(-charitableDeduction)}</div>
                    {runningTotalAmountsWithStrategies &&
                        <div className={"span-3"}>{formatCurrency(-charitableDeduction)}</div>}
                </div>}

                {additionalExemptionsOrDeductions.map(({label, amount, amountWithStrategies}) =>
                    <div className={lineItemRowClass} key={label}>
                        <div className={"span-1"}>{label}</div>
                        <div
                            className={"span-2"}>{amount === 0 ? formatCurrency(amount) : formatCurrency(-amount)}</div>
                        {amountWithStrategies !== undefined && <div className={"span-3"}>{amountWithStrategies === 0
                            ? formatCurrency(amountWithStrategies)
                            : formatCurrency(-amountWithStrategies)}</div>
                        }
                    </div>
                )}

                {gtgtExemptions?.map(({exemptionAmount, description}) => (
                    <div className={lineItemRowClass} key={description}>
                        <div className="span-1"/>
                        <div className="span-2"/>
                        <div className="span-3 amt-to-beneficiaries">
                            <div className={"text-content"}>{formatCurrency(-exemptionAmount)}</div>
                        </div>
                        <div className="span-4 strategy-description">
                            <div className={"text-content"}>{description}</div>
                        </div>
                    </div>
                ))}

                <div className={`${lineItemRowClass} subsection-header`}>
                    <div className={"span-1"}>Taxable Estate</div>
                    <div className={"span-2"}>{formatCurrency(runningTotalAmounts.taxableEstateValue)}</div>
                    {runningTotalAmountsWithStrategies &&
                        <div
                            className={"span-3"}>{formatCurrency(runningTotalAmountsWithStrategies.taxableEstateValue)}</div>}
                </div>

                <div className={lineItemRowClass}>
                    <div className={"span-1"}>* {taxRateLabel} Tax Rate</div>
                    <div
                        className={"span-2"}>{formatPercentWithTwoDecimals(runningTotalAmounts.effectiveTaxRate / 100)}</div>
                    {runningTotalAmountsWithStrategies &&
                        <>
                            <div className={"span-3"}>
                                {formatPercentWithTwoDecimals(runningTotalAmountsWithStrategies.effectiveTaxRate / 100)}
                            </div>
                            <div className="span-4 strategy-description">
                                <div className={"text-content sor-description"}>{sorDescription}</div>
                            </div>
                        </>
                    }
                </div>

                <div className={`${lineItemRowClass} subsection-header`}>
                    <div className={"span-1"}>Estimated {taxType} Estate Tax</div>
                    <div className={"span-2"}>{formatCurrency(runningTotalAmounts.estimatedEstateTax)}</div>
                    {runningTotalAmountsWithStrategies &&
                        <div
                            className={"span-3"}>{formatCurrency(runningTotalAmountsWithStrategies.estimatedEstateTax)}</div>}
                </div>
            </div>}
    </div>;
}